import React, { useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Input } from "reactstrap";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonSearch from "Components/Common/CommonSearch";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import { GetUserData, showToastMessage } from "common/service/CommonService";
import { useQueryClient } from "@tanstack/react-query";
import { assignSubjectColumns } from "../helper/ambulatoryvisitconstants";


interface AssignSubjectsModalProps {
  isOpen: boolean;
  onClose: () => void;
  visit: any;
}

const AssignSubjectsModal: React.FC<AssignSubjectsModalProps> = ({
  isOpen,
  onClose,
  visit
}) => {
  const queryClient = useQueryClient();
  const studyid = Number(sessionStorage.getItem("activestudyid") || 1);
  const [selectedSubjects, setSelectedSubjects] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [lastLoadedVisitId, setLastLoadedVisitId] = useState<number | null>(null);

  const { assignSubjectsAmbulatoryVisit, SelectAllAssignedSubjects, SelectAllEligibleSubjects } = useAmbulatoryVisitService({
    studyid: studyid,
    visitid: Number(visit?.visitid),
    currentPage: currentPage,
    pageSize: 10,
    searchtext: searchText,
  });

  const enrolledSubjects = useMemo(() => {
    return (SelectAllEligibleSubjects?.data?.data as any[]) || [];
  }, [SelectAllEligibleSubjects?.data]);

  const totalRecords = useMemo(() => {
    return (enrolledSubjects[0]?.totalcount) || (SelectAllEligibleSubjects?.data as any)?.totalrecords || enrolledSubjects.length || 0;
  }, [enrolledSubjects, SelectAllEligibleSubjects?.data]);

  // Pre-populate selections from API when modal opens and assigned subjects are loaded
  useEffect(() => {
    if (isOpen && visit && SelectAllAssignedSubjects.data) {
      const currentVisitId = Number(visit.visitid);
      if (lastLoadedVisitId !== currentVisitId) {
        const assigned = (SelectAllAssignedSubjects.data?.data as any[]) || [];
        const mapped = assigned.map((sub: any) => ({
          subjectno: String(sub.subjectno),
          volunteerid: sub.volunteerid,
          volunteerassignmentid: sub.volunteerassignmentid,
          fullname: sub.fullname || sub.subjectName,
          initials: sub.initials,
          barcode: sub.barcode || sub.screeningNo,
          gendertext: sub.gendertext,
          age: sub.age,
          createddate: sub.createddate,
          site: sub.site
        }));
        setSelectedSubjects(mapped);
        setLastLoadedVisitId(currentVisitId);
      }
    }
  }, [isOpen, visit, SelectAllAssignedSubjects.data, lastLoadedVisitId]);

  useEffect(() => {
    if (!isOpen) {
      setLastLoadedVisitId(null);
      setSelectedSubjects([]);
      setCurrentPage(1);
      setSearchText("");
    }
  }, [isOpen]);

  const selectedSubjectNos = useMemo(() => {
    return selectedSubjects.map((s) => String(s.subjectno));
  }, [selectedSubjects]);

  const handleSelectToggle = (row: any) => {
    setSelectedSubjects((prev) => {
      const exists = prev.some((s) => String(s.subjectno) === String(row.subjectno));
      if (exists) {
        return prev.filter((s) => String(s.subjectno) !== String(row.subjectno));
      } else {
        return [...prev, row];
      }
    });
  };

  const handleSave = () => {
    const userData = GetUserData();
    const payload = {
      visitid: Number(visit?.visitid),
      studyid: Number(studyid),
      createdby: Number(userData?.userid || ""),
      subjectlist: selectedSubjects.map((sub) => ({
        subjectno: String(sub.subjectno),
        volunteerassignmentid: Number(sub.volunteerassignmentid)
      }))
    };

    assignSubjectsAmbulatoryVisit.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["selectall-ambulatory-visit"] });
        queryClient.invalidateQueries({ queryKey: ["selectall-assigned-subjects"] });
        showToastMessage("success", response?.message || "Subjects assigned successfully!");
        onClose();
      },
      onError: (error: any) => {
        showToastMessage("error", error || "Failed to assign subjects");
      }
    });
  };

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setCurrentPage(1);
  };

  const isLoading = SelectAllEligibleSubjects.isLoading || SelectAllAssignedSubjects.isLoading;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="md"
      backdrop="static"
      className="common_modal"
      centered
    >
      <ModalHeader toggle={onClose} className="fw-bold border-bottom p-3">
        Assign Subjects to {visit?.visitnumber || "--"}
      </ModalHeader>

      <ModalBody style={{ maxHeight: "500px", overflowY: "auto" }}>
        <div className="d-flex align-items-center justify-content-between gap-2 p-2 mb-2">
          <CommonSearch
            onSearch={onSearch}
            searchText={searchText}
            placeholder="Search..."
          />
        </div>

        {isLoading && enrolledSubjects.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center py-5">
            <Spinner color="primary" />
          </div>
        ) : enrolledSubjects.length === 0 ? (
          <div className="text-center py-4 text-muted">
            {searchText ? "No search results found." : "No enrolled subjects found for this study."}
          </div>
        ) : (
          <CommonTableIndexWise
            columns={assignSubjectColumns(handleSelectToggle, selectedSubjectNos)}
            data={enrolledSubjects}
            loading={SelectAllEligibleSubjects.isLoading || SelectAllEligibleSubjects.isFetching || SelectAllEligibleSubjects.isPending || SelectAllAssignedSubjects.isFetching}
            pageSize={10}
            currentPage={currentPage}
            totalRecords={totalRecords}
            onPageChange={(p) => setCurrentPage(p)}
            paginationShow={true}
            hideAction={true}
          />
        )}
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button outline onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          outline
          onClick={handleSave}
          disabled={isLoading || assignSubjectsAmbulatoryVisit.isPending}
        >
          {assignSubjectsAmbulatoryVisit.isPending && <Spinner size="sm" className="me-2" />}
          Assign
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignSubjectsModal;
